import React, { useEffect, useMemo, useRef, useState } from "react";
import { FaArrowRight, FaChevronRight, FaTimes } from "react-icons/fa";
import { NavLink, useLocation } from "react-router-dom";

import { APP_LINK_ICONS, getSectionsForRole } from "../data/navigationData";
import { slugify } from "../utils/navigationUtils";

const STORAGE_KEY = "monAppliTournoiMegaMenuLastSection";

const MegaMenu = ({ roleId, onClose }) => {
  const location = useLocation();
  const dialogRef = useRef(null);

  const sections = useMemo(() => {
    return getSectionsForRole(roleId).map((section) => ({
      ...section,
      cards: section.links.map((title) => {
        const Icon = APP_LINK_ICONS[title];

        return {
          title,
          to: `/${slugify(title)}`,
          Icon,
        };
      }),
    }));
  }, [roleId]);

  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    if (!sections.length) return;

    const currentSlug = location.pathname.split("/")[1] || "";

    const indexFromPath = sections.findIndex((section) =>
      section.cards.some((card) => slugify(card.title) === currentSlug)
    );

    if (indexFromPath >= 0) {
      setActiveIndex(indexFromPath);
      return;
    }

    const storedIndex = Number(localStorage.getItem(STORAGE_KEY));

    if (
      Number.isFinite(storedIndex) &&
      storedIndex >= 0 &&
      storedIndex < sections.length
    ) {
      setActiveIndex(storedIndex);
      return;
    }

    setActiveIndex(0);
  }, [location.pathname, sections]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, String(activeIndex));
  }, [activeIndex]);

  useEffect(() => {
    const firstFocusable = dialogRef.current?.querySelector("button, a");
    firstFocusable?.focus?.();
  }, []);

  const activeSection = sections[activeIndex] || sections[0];

  return (
    <section
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-label="Navigation dans l'application"
      className="
        flex h-[100dvh] w-full max-w-full flex-col overflow-hidden bg-white text-slate-950 shadow-2xl
        dark:bg-slate-950 dark:text-white
        sm:max-w-[480px]
        lg:h-auto lg:max-h-[calc(100dvh-2rem)] lg:max-w-[1180px] lg:rounded-[2rem] lg:border lg:border-slate-200 lg:dark:border-white/10
      "
    >
      <div
        className="
          flex shrink-0 items-start justify-between gap-3 border-b border-slate-200
          bg-slate-50 px-4 py-4 dark:border-white/10 dark:bg-slate-900
          sm:px-5 lg:px-6 lg:py-5
        "
      >
        <div className="min-w-0">
          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-orange-600 dark:text-orange-300 sm:text-[11px]">
            Navigation application
          </p>

          <h2 className="mt-1 text-lg font-bold leading-tight text-slate-950 dark:text-white lg:text-2xl">
            Accès rapide
          </h2>

          <p className="mt-1 hidden max-w-2xl text-sm text-slate-600 dark:text-slate-300 lg:block">
            Retrouvez les espaces utiles selon votre rôle : compétition,
            arbitrage, résultats, administration et outils publics.
          </p>
        </div>

        <button
          type="button"
          onClick={onClose}
          className="
            inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl
            border border-slate-200 bg-white text-slate-700 shadow-sm transition
            hover:bg-slate-100 hover:text-orange-600
            focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-400
            dark:border-white/10 dark:bg-white/10 dark:text-white dark:hover:bg-white/20
            sm:h-11 sm:w-11
          "
          aria-label="Fermer le menu"
        >
          <FaTimes className="h-4 w-4" />
        </button>
      </div>

      {/* Mobile / tablette */}
      <div className="flex-1 overflow-y-auto bg-white px-4 py-4 dark:bg-slate-950 sm:px-5 lg:hidden">
        <div className="space-y-5 pb-8">
          {sections.map((section) => (
            <section key={section.key} className="min-w-0">
              <div className="mb-3 min-w-0">
                <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-orange-600 dark:text-orange-300">
                  {section.eyebrow}
                </p>

                <h3 className="mt-0.5 text-base font-bold leading-tight text-slate-950 dark:text-white">
                  {section.label}
                </h3>

                {section.description && (
                  <p className="mt-1 text-xs leading-relaxed text-slate-500 dark:text-slate-400">
                    {section.description}
                  </p>
                )}
              </div>

              <div className="grid gap-2">
                {section.cards.map(({ title, to, Icon }) => (
                  <NavLink
                    key={title}
                    to={to}
                    onClick={onClose}
                    className={({ isActive }) =>
                      `
                        group flex min-h-14 w-full min-w-0 items-center gap-3 rounded-2xl border px-3 py-2.5
                        transition focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-400
                        ${
                          isActive
                            ? "border-orange-400 bg-orange-50 text-orange-800 dark:bg-orange-500/20 dark:text-white"
                            : "border-slate-200 bg-white text-slate-800 hover:border-orange-200 hover:bg-orange-50 hover:text-orange-700 dark:border-white/10 dark:bg-white/[0.05] dark:text-white dark:hover:border-orange-300/40 dark:hover:bg-white/[0.08]"
                        }
                      `
                    }
                  >
                    <span
                      className="
                        flex h-10 w-10 shrink-0 items-center justify-center rounded-xl
                        bg-orange-100 text-orange-600 ring-1 ring-orange-200
                        transition group-hover:bg-orange-500 group-hover:text-white
                        dark:bg-orange-500/15 dark:text-orange-300 dark:ring-orange-300/20
                      "
                    >
                      {Icon ? (
                        <Icon className="h-5 w-5" />
                      ) : (
                        <FaArrowRight className="h-4 w-4" />
                      )}
                    </span>

                    <span className="min-w-0 flex-1 text-sm font-bold leading-tight">
                      {title}
                    </span>

                    <FaChevronRight className="h-3.5 w-3.5 shrink-0 text-slate-400 transition group-hover:translate-x-0.5 group-hover:text-orange-500 dark:text-slate-500" />
                  </NavLink>
                ))}
              </div>
            </section>
          ))}
        </div>
      </div>

      {/* Desktop */}
      <div className="hidden min-h-0 flex-1 overflow-hidden lg:grid lg:grid-cols-[280px_1fr]">
        <aside className="min-w-0 shrink-0 border-r border-slate-200 bg-white p-5 dark:border-white/10 dark:bg-white/[0.02]">
          <div className="grid grid-cols-1 gap-2">
            {sections.map((section, index) => {
              const active = index === activeIndex;

              return (
                <button
                  key={section.key}
                  type="button"
                  onClick={() => setActiveIndex(index)}
                  className={`min-w-0 rounded-2xl border p-4 text-left transition ${
                    active
                      ? "border-orange-400 bg-orange-50 text-orange-800 shadow-sm dark:border-orange-400/70 dark:bg-orange-500 dark:text-white"
                      : "border-slate-200 bg-slate-50 text-slate-700 hover:border-orange-200 hover:bg-orange-50 hover:text-orange-700 dark:border-white/10 dark:bg-white/5 dark:text-slate-200 dark:hover:border-white/20 dark:hover:bg-white/10"
                  }`}
                >
                  <div className="truncate text-[10px] font-bold uppercase tracking-[0.16em] opacity-80">
                    {section.eyebrow}
                  </div>

                  <div className="mt-1 truncate text-base font-bold leading-tight">
                    {section.label}
                  </div>

                  <div className="mt-1 text-xs leading-snug opacity-80">
                    {section.description}
                  </div>
                </button>
              );
            })}
          </div>
        </aside>

        <main className="min-h-0 overflow-y-auto bg-slate-50/80 p-7 dark:bg-transparent">
          <div className="mb-5 flex items-end justify-between gap-4">
            <div className="min-w-0">
              <p className="text-sm font-bold text-orange-600 dark:text-orange-300">
                {activeSection?.eyebrow}
              </p>

              <h3 className="truncate text-2xl font-bold leading-tight text-slate-950 dark:text-white">
                {activeSection?.label}
              </h3>
            </div>

            <p className="max-w-xl text-sm text-slate-600 dark:text-slate-300">
              {activeSection?.description}
            </p>
          </div>

          {activeSection?.cards?.length ? (
            <ul className="grid grid-cols-2 gap-3 xl:grid-cols-3">
              {activeSection.cards.map(({ title, to, Icon }) => (
                <li key={title} className="min-w-0">
                  <NavLink
                    to={to}
                    onClick={onClose}
                    className={({ isActive }) =>
                      `
                        group relative flex min-h-[104px] min-w-0 overflow-hidden rounded-3xl border p-4
                        transition focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-400
                        ${
                          isActive
                            ? "border-orange-400 bg-orange-50 text-orange-800 dark:bg-orange-500/15 dark:text-white"
                            : "border-slate-200 bg-white text-slate-800 hover:-translate-y-0.5 hover:border-orange-200 hover:bg-orange-50 hover:text-orange-700 dark:border-white/10 dark:bg-white/[0.06] dark:text-white dark:hover:border-orange-300/50 dark:hover:bg-white/[0.09]"
                        }
                      `
                    }
                  >
                    <div className="relative z-10 flex w-full min-w-0 items-center gap-4 text-left">
                      <div
                        className="
                          flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl
                          bg-orange-100 text-orange-600 ring-1 ring-orange-200
                          transition group-hover:scale-105 group-hover:bg-orange-500 group-hover:text-white
                          dark:bg-orange-500/15 dark:text-orange-300 dark:ring-orange-300/20
                        "
                      >
                        {Icon ? (
                          <Icon className="h-7 w-7" />
                        ) : (
                          <FaArrowRight className="h-5 w-5" />
                        )}
                      </div>

                      <div className="min-w-0 flex-1">
                        <div className="break-words text-base font-bold leading-tight">
                          {title}
                        </div>

                        <div className="mt-1 flex items-center gap-2 text-sm text-slate-500 dark:text-slate-300">
                          Ouvrir
                          <FaArrowRight className="h-3 w-3 transition group-hover:translate-x-1" />
                        </div>
                      </div>
                    </div>
                  </NavLink>
                </li>
              ))}
            </ul>
          ) : (
            <div className="rounded-3xl border border-slate-200 bg-white p-5 text-sm text-slate-600 dark:border-white/10 dark:bg-white/[0.04] dark:text-slate-300">
              Aucun lien disponible pour cette section.
            </div>
          )}
        </main>
      </div>
    </section>
  );
};

export default MegaMenu;